import { useEffect } from 'react';
import { useJournal } from '../api/journal';
import { DAILY_REWARD } from '../journal/taskRewards';
import { showToast, useToastStore } from './toastStore';
import { useCelebrationQueue } from './celebrationQueue';

/**
 * One receipt when dailies complete (player review 2026-09-27: Shells went 500 → 650 with no
 * on-screen reason). Mounted once in AppShell.
 *
 * All state is MODULE-level, never refs: under <StrictMode> an effect's simulated unmount would
 * otherwise cancel a pending flush and lose the receipt (CLAUDE.md "Lesson 1"). The first payload
 * of a day only SEEDS (same guard as useAchievementCelebrations), so opening the app with dailies
 * already done raises nothing. A new day (dailyResetAt moves) re-seeds silently.
 */
const seen = new Map<string, boolean>();
let seededFor: string | null = null;
let pending: string[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_MS = 1500;

/** Set while `flush()` is waiting for the toast/celebration surfaces to go quiet — a combined
 *  unsubscribe for BOTH stores' listeners (see `armWait` below). Non-null means a wait is
 *  already armed — a second batch arriving during that wait must NOT arm a second one (its
 *  labels are already in `pending` and will drain on the one existing wait); without this, a
 *  second pair of subscriptions' correctness would rest on zustand's listener-notification
 *  order, and `resetDailyReceipts()` would have no way to clean up a dangling one either. */
let unsubscribeWait: (() => void) | null = null;

/**
 * True while the receipt must hold off: an ordinary toast is showing, OR the celebration overlay
 * (`docs/DESIGN.md` §21 — level-up, first badge, collection complete) either owns the screen right
 * now (`current`) or is holding one for later (`pending.length`, e.g. while `blocked` during a
 * battle route — `current` is null then, but the celebration is still coming). A receipt that
 * fired under, or right before, a full-screen celebration is dimmed, untappable and gone before
 * the player ever sees it (the real-browser finding this guards). Waiting for `pending` too, not
 * just `current`, is what makes the receipt land AFTER the player dismisses every held
 * celebration, not just the first one.
 */
function isBusy(): boolean {
  if (useToastStore.getState().toast) return true;
  const { current, pending: celebrationsPending } = useCelebrationQueue.getState();
  return current != null || celebrationsPending.length > 0;
}

/** Arms the one wait, subscribed to BOTH stores (either can flip `isBusy()` from true to false —
 *  a toast clearing, a celebration being dismissed, or the queue unblocking and draining its
 *  `pending`). Re-checks `isBusy()` fresh on every notification from either store; only tears
 *  down and calls `flush()` again once it reads false from both. */
function armWait(): void {
  if (unsubscribeWait) return; // already armed — see the doc comment above `unsubscribeWait`.
  const recheck = () => {
    if (isBusy()) return;
    unsubscribeWait?.();
    unsubscribeWait = null;
    flush();
  };
  const unsubToast = useToastStore.subscribe(recheck);
  const unsubCelebration = useCelebrationQueue.subscribe(recheck);
  unsubscribeWait = () => { unsubToast(); unsubCelebration(); };
}

/** Forget the day's baseline and drop any pending receipt — on logout (MeScreen) and in tests. */
export function resetDailyReceipts(): void {
  seen.clear(); seededFor = null; pending = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  unsubscribeWait?.();
  unsubscribeWait = null;
}

export function newlyDone(
  seenMap: Map<string, boolean>, dailies: { code: string; label: string; done: boolean }[],
): string[] {
  const out: string[] = [];
  for (const t of dailies) {
    if (t.done && seenMap.get(t.code) === false) out.push(t.label);
    seenMap.set(t.code, t.done);
  }
  return out;
}

export function receiptText(labels: string[]): string {
  const amount = DAILY_REWARD.amount ?? 0; // TaskReward.amount is optional in general; DAILY_REWARD always sets it.
  if (labels.length === 1) return `Daily done · ${labels[0]} · +${amount} Shells`;
  return `${labels.length} dailies done · +${labels.length * amount} Shells`;
}

function flush(): void {
  flushTimer = null;
  if (pending.length === 0) return;
  // One toast at a time (SCREENS.md "Global additions"), and never under/behind the celebration
  // overlay (the real-browser finding this guards): wait for whatever is showing or held — e.g.
  // the skill-completion toast that caused this daily, or a level-up card the same completion
  // just queued — rather than replacing or being swallowed by it.
  if (isBusy()) {
    armWait();
    return;
  }
  const labels = pending;
  pending = [];
  showToast(receiptText(labels));
}

export function useDailyReceipts(): void {
  const journal = useJournal();
  useEffect(() => {
    const data = journal.data;
    if (!data) return;
    const day = String(data.dailyResetAt);
    if (seededFor !== day) {
      seen.clear();
      for (const t of data.dailies) seen.set(t.code, t.done);
      seededFor = day;
      return;
    }
    const fresh = newlyDone(seen, data.dailies);
    if (fresh.length === 0) return;
    pending = [...pending, ...fresh];
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, BATCH_MS);
  }, [journal.data]);
}
